/**
 * reportDelivery.ts — portal seam over the Phase-2 report Composer + DELIVERY
 * Cloud Functions (Slice 1 + Slice 2).
 *
 * Three thin callable wrappers + pure capability/validation helpers:
 *
 *   composeReport({ clientId, projectId, tier })
 *     → Slice 1. Generates an UNGATED draft PDF, uploads it, registers the
 *       artefact + persists clients/{id}/reports/{tier}_v{n} with status='draft'.
 *       Returns { runId, version_n, storagePath, cached, pdfRendered }.
 *
 *   approveReport({ clientId, projectId, tier, runId })
 *     → Slice 2. The human approve-before-deliver gate. draft → approved
 *       (assignee | admin). The future dictate→regenerate loop (Slice 2b)
 *       slots in BEFORE approve — see the SEAM note below.
 *
 *   deliverReport({ clientId, projectId, tier, runId })
 *     → Slice 2. The server-gated release. Refuses unless approved; re-checks
 *       money-before-delivery (THE SOLE money authority is the server's
 *       projects/{id}.gateState.moneyPaid verdict) + curation; posts the
 *       deliverable_event into the client conversation; flips status→delivered.
 *       Returns { delivered, messageId, ledgerEntryId, reportId }.
 *
 * The portal NEVER decides money/curation client-side — the server is
 * authoritative. A blocked delivery surfaces the server's failed-precondition
 * message (e.g. "Blocked: stage payment not received (stage1)").
 *
 * PURE/IMPURE split (mirrors gateOverride.ts): the capability predicate +
 * tier-coercion are pure + unit-tested; the three CF calls are the thin seam.
 *
 * 2026-06-04 — Anil (Phase-2 Slice 2).
 */
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db, functions } from './firebase';
import type { Role } from './roles';

/* ════════════════════ Capability resolution (PURE) ════════════════════ */

/**
 * Who may GENERATE a report? Per SC-037 (curation gating relaxed), generation
 * is NOT assignee-gated — any team member may generate any client's draft. The
 * composeReport CF re-checks isTeamCaller server-side regardless.
 */
export function canGenerateReport(role: Role | null | undefined): boolean {
  return role === 'admin' || role === 'staff';
}

/**
 * Who may APPROVE + DELIVER a report? The curation authority — the project
 * assignee OR an admin (no tier-fallback). This is the UX predicate; both CFs
 * re-check assignee|admin server-side. `canCommitToProject` is the existing
 * assignee+admin predicate the project surface already uses — callers pass its
 * result here so we have ONE source of truth for "may release".
 */
export function canDeliverReport(mayCommit: boolean): boolean {
  return mayCommit === true;
}

/**
 * Wired report tiers — have a composer template / gate map. L1A + L2B (slice 2)
 * + L1C (2026-06-23, plan-review verdict). Other tiers are not yet deliverable
 * through this seam.
 */
export const DELIVERABLE_TIERS = ['L1A', 'L2B', 'L1C'] as const;
export type DeliverableTier = (typeof DELIVERABLE_TIERS)[number];

/**
 * Coerce a project's tier code to a Slice-2 deliverable tier, or null when the
 * tier is not yet wired. The project's `tierCode` is the full code (e.g.
 * 'L2B'); we accept exactly the wired set.
 */
export function toDeliverableTier(
  tierCode: string | null | undefined,
): DeliverableTier | null {
  if (!tierCode) return null;
  const t = String(tierCode).toUpperCase().trim();
  return (DELIVERABLE_TIERS as readonly string[]).includes(t)
    ? (t as DeliverableTier)
    : null;
}

/* ════════════════════ Report status (PURE) ════════════════════ */

export type ReportStatus = 'draft' | 'approved' | 'delivered';

/** Normalise a report doc's status field (absent/unknown → 'draft'). */
export function reportStatusOf(
  report: { status?: string } | null | undefined,
): ReportStatus {
  const s = report?.status;
  return s === 'approved' || s === 'delivered' ? s : 'draft';
}

/* ════════════════════ Callable seam (impure) ════════════════════ */

export interface ComposeInput {
  clientId: string;
  projectId: string;
  tier: DeliverableTier;
  forceRegen?: boolean;
  /**
   * M6 Inc-3 — OPTIONAL report phase (Project → Report(phase) → Version). When
   * present the CF keys the version WITHIN the phase (`{tier}__{phaseId}_v{n}`);
   * absent → the legacy flat `{tier}_v{n}` (LIVE L1A/L2B path unchanged).
   */
  phaseNo?: number;
  phaseId?: string;
  phaseLabel?: string;
}
export interface ComposeResult {
  runId: string;
  version_n: number;
  storagePath: string | null;
  cached: boolean;
  pdfRendered: boolean;
}

/** Slice 1 — generate (or reuse) the ungated draft report PDF. */
export async function composeReport(input: ComposeInput): Promise<ComposeResult> {
  if (!input.clientId) throw new Error('No client linked to this project.');
  if (!input.projectId) throw new Error('No project selected.');
  if (!input.tier) throw new Error('This tier is not yet wired for report generation.');
  const fn = httpsCallable<ComposeInput, ComposeResult>(functions, 'composeReport');
  const res = await fn(input);
  return res.data;
}

export interface DeliverySeamInput {
  clientId: string;
  projectId: string;
  tier: DeliverableTier;
  /** Identify which generated report. runId is canonical; versionN is a fallback. */
  runId?: string;
  versionN?: number;
}

export interface ApproveResult {
  ok: boolean;
  status: 'approved';
  reportId: string;
}

/**
 * Slice 2 — the human approve gate (draft → approved). assignee | admin.
 *
 * SEAM — Slice 2b dictate→regenerate loop slots in BEFORE approve: a future
 * `regenerateReport(dictation)` step would re-run composeReport with owner
 * dictation, mint a NEW draft version, and THIS approve gate would run against
 * that fresh version. For Slice 2 it is a clean draft → approved flip.
 */
export async function approveReport(input: DeliverySeamInput): Promise<ApproveResult> {
  if (!input.clientId || !input.projectId || !input.tier) {
    throw new Error('Missing client / project / tier.');
  }
  const fn = httpsCallable<DeliverySeamInput, ApproveResult>(functions, 'approveReport');
  const res = await fn(input);
  return res.data;
}

export interface DeliverResult {
  delivered: boolean;
  messageId: string;
  ledgerEntryId: string;
  reportId: string;
}

/**
 * Slice 2 — the server-gated release into the client conversation. The server
 * re-checks money-before-delivery + curation; a block throws a
 * failed-precondition whose `.message` names the reason ("Blocked: stage
 * payment not received …"). Callers surface that message verbatim.
 */
export async function deliverReport(input: DeliverySeamInput): Promise<DeliverResult> {
  if (!input.clientId || !input.projectId || !input.tier) {
    throw new Error('Missing client / project / tier.');
  }
  const fn = httpsCallable<DeliverySeamInput, DeliverResult>(functions, 'deliverReport');
  const res = await fn(input);
  return res.data;
}

/* ════════════════════ Slice 3 — signed download URL (impure) ════════════════════ */

export interface DownloadUrlInput {
  clientId: string;
  projectId: string;
  tier: DeliverableTier;
  /** Identify which generated report. runId is canonical; versionN is a fallback. */
  runId?: string;
  versionN?: number;
}

export interface DownloadUrlResult {
  /** Short-lived (10 min) signed READ URL for the delivered report PDF. */
  url: string;
  /** Epoch ms the URL expires (now + TTL). */
  expiresAt: number;
  /** Suggested download file name (e.g. "L2B_v3.pdf"). */
  fileName: string;
}

/**
 * Slice 3 — mint a short-lived signed download URL for a DELIVERED report.
 *
 * The server (getReportDownloadUrl CF) is the SOLE authority: it re-checks the
 * caller is team OR the report's own client, the report is delivered, and the
 * report↔project↔client FK chain. The portal NEVER decides entitlement
 * client-side — a denied request throws a permission-denied / failed-precondition
 * whose `.message` is surfaced verbatim. We do NOT cache the URL (it expires);
 * a fresh mint is requested on each open.
 */
export async function getReportDownloadUrl(
  input: DownloadUrlInput,
): Promise<DownloadUrlResult> {
  if (!input.clientId || !input.projectId || !input.tier) {
    throw new Error('Missing client / project / tier for the report download.');
  }
  const fn = httpsCallable<DownloadUrlInput, DownloadUrlResult>(
    functions,
    'getReportDownloadUrl',
  );
  const res = await fn(input);
  return res.data;
}

/**
 * PURE — coerce a chip attachment's `tier` to a deliverable tier for the
 * download call (the chip carries the raw tier string). Returns null when the
 * attachment is not a wired report tier — the caller then shows a "not yet
 * available" message rather than calling the CF with a bad tier.
 */
export function downloadTierFromAttachment(
  tier: string | null | undefined,
): DeliverableTier | null {
  return toDeliverableTier(tier);
}

/* ════════════════════ Draft artefact URL (impure) ════════════════════ */

/**
 * Open the STORED draft PDF (Pass-1 §1.3 fix).
 *
 * `getReportDownloadUrl` (the CF above) only serves DELIVERED reports, so it
 * 404s for a DRAFT ("No report found for client {cid} tier L1A"). A draft's PDF
 * is already on disk: the report doc carries `artefact_file_id`, and the
 * artefact metadata doc at `projects/{pid}/artefacts/{fileId}` carries the
 * working `downloadURL` (the same URL that opens the PDF tab elsewhere). We read
 * that doc and return its `downloadURL`.
 *
 * Throws a readable Error when the fileId is missing, the artefact doc is gone,
 * or it has no `downloadURL` — the caller surfaces it via onMsg.
 */
export async function getDraftArtefactUrl(
  projectId: string,
  fileId: string | null | undefined,
): Promise<string> {
  if (!projectId) throw new Error('No project selected.');
  if (!fileId) throw new Error('This draft has no stored artefact yet — regenerate it.');
  const snap = await getDoc(doc(db, 'projects', projectId, 'artefacts', fileId));
  if (!snap.exists()) {
    throw new Error('The draft artefact could not be found — regenerate the draft.');
  }
  const url = (snap.data() as { downloadURL?: unknown }).downloadURL;
  if (typeof url !== 'string' || !url) {
    throw new Error('The draft artefact has no download URL — regenerate the draft.');
  }
  return url;
}

/* ════════════════════ Report-doc listener (team-only read) ════════════════════ */

/**
 * A generated report record (clients/{cid}/reports/{tier}_v{n}). CF-written
 * (Composer + delivery seam); team-readable. The portal listens so the
 * Composer & delivery panel reflects draft / approved / delivered + the latest
 * runId/version for the approve+deliver call.
 */
export interface ReportDoc {
  id: string;
  run_id?: string;
  project_id?: string;
  tier?: string;
  version_n?: number;
  storage_path?: string | null;
  artefact_file_id?: string | null;
  status?: ReportStatus;
  generated_at?: string;
}

/**
 * Stream the reports for a project. We query the client's reports subcollection
 * filtered by project_id (every report doc carries it since Slice 1).
 *
 * `deliveredOnly` (Slice 3) constrains the query to delivered reports. A CLIENT
 * MUST pass this true — firestore.rules only lets a client READ a report whose
 * `status == 'delivered'`, and Firestore evaluates a `list` rule against the
 * QUERY: an unconstrained client query (project_id only) would be DENIED
 * because it could return a draft/approved doc. Team callers may leave it false
 * to see every status (the Composer/delivery panel). Requires the composite
 * index (project_id ASC, status ASC) for the constrained query.
 */
export function listenProjectReports(
  clientId: string,
  projectId: string,
  cb: (reports: ReportDoc[]) => void,
  onErr?: (e: Error) => void,
  deliveredOnly = false,
): Unsubscribe {
  if (!clientId || !projectId) {
    cb([]);
    return () => {};
  }
  const col = collection(db, 'clients', clientId, 'reports');
  const q = deliveredOnly
    ? query(col, where('project_id', '==', projectId), where('status', '==', 'delivered'))
    : query(col, where('project_id', '==', projectId));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ReportDoc, 'id'>) }))),
    (e) => onErr?.(e),
  );
}

/**
 * PURE — pick the report to act on from a list (newest version first). The
 * Composer & delivery panel acts on the highest-version report for the tier.
 */
export function latestReportForTier(
  reports: ReportDoc[],
  tier: DeliverableTier,
): ReportDoc | null {
  const matching = reports.filter((r) => (r.tier || '').toUpperCase() === tier);
  if (!matching.length) return null;
  return matching.slice().sort((a, b) => (b.version_n || 0) - (a.version_n || 0))[0];
}
